import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './Pricing.css';
import PricingHeader from '../../components/pricing/PricingHeader';
import PriceCalculator from '../../components/pricing/PriceCalculator';
import WhyChooseOurPms from '../../components/WhyChooseOurPms';
import TitleAndContentCard from '../../components/TitleAndContentCard'
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function Pricing() {
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();

    const features = [
        { title: t('Modular by Design'), content: t('Only pay for what you need. Prostay’s modular structure allows you to choose the tools that match your property operations, both on-premise or cloud-based.') },
        { title: t('Scales with You'), content: t('Whether you manage 10 rooms or 200, our pricing adjusts to your property size and complexity. Grow your business confidently without unexpected jumps in cost.') },
        { title: t('Transparent & Fair'), content: t('No hidden fees, no confusing contracts, and no forceable commitments. Just clear, predictable pricing designed to support long-term success and partnership.') },
    ];

    return (
        <div>
            <Helmet>
                <title>Hotel Software Price & Hotel Management Software Plans - Prostay</title>
                <meta name="description" content="Transparent hotel software pricing. Choose only the modules you need, and scale with your property—no hidden fees, no surprises." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/pricing/"} />

                <link rel="alternate" href="https://www.prostay.com/en/pricing/" />
                <link rel="alternate" href="https://www.prostay.com/es/pricing/" />

                <meta property="og:title" content="Hotel Software Price & Hotel Management Software Plans - Prostay" />
                <meta property="og:description" content="Transparent hotel software pricing. Choose only the modules you need, and scale with your property—no hidden fees, no surprises." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/pricing/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Hotel Software Price & Hotel Management Software Plans - Prostay" />
                <meta name="twitter:description" content="Transparent hotel software pricing. Choose only the modules you need, and scale with your property—no hidden fees, no surprises." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />


            </Helmet>
            <div className='pricing'>
                <PricingHeader />
                <PriceCalculator />
                <div className='d-flex justify-content-center'>
                    <div className=' page-content'>
                        <div className='d-flex flex-column'>
                            <h3>{t('Discover hotel software pricing that’s transparent, flexible, and scalable')}</h3>
                            <div className='features-carousel-wrapper'>
                                <div className='d-flex gap-3 features-carousel pb-5'>
                                    {
                                        features.map((feature, index) =>
                                            <TitleAndContentCard title={feature.title} content={feature.content} width={384} />
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <WhyChooseOurPms
                    headerTitle="Take Charge with Hotel PMS Today!"
                    headerDescription="Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests."
                    sectionTitle={t("Upfront Hotel Software Pricing - Zero Hidden Fees")}
                    sectionDescription={t("We believe in straightforward pricing that puts you in control. No hidden fees, no unexpected costs—just flexible, scalable plans tailored to your property’s needs. Below are answers to common pricing questions to help you make an informed decision.")}
                    cards={[
                        { title: t("Are there any hidden fees in Prostay’s pricing?"), description: t("No. Prostay’s pricing is fully transparent. You only pay for the modules you select, and there are no surprise costs or additional transaction fees. You’re always in control of your plan.") },
                        { title: t("Can I customize my Prostay subscription based on my property’s needs?"), description: t("Yes! Prostay offers a modular pricing structure, allowing you to choose only the features you need. Whether you’re a small boutique hotel or a large resort, you can scale up or down as your business evolves.") },
                        { title: t("Does Prostay charge per user or per property?"), description: t("Our pricing is based on the size of your property and the modules you select, not on the number of users. You can add unlimited staff members without incurring additional costs.") },
                        { title: t("Are there any long-term contracts or cancellation fees?"), description: t("No. Prostay offers flexible subscription plans with no long-term commitments. You can cancel or modify your plan at any time without penalty.") },
                        { title: t("How does Prostay’s pricing compare to other PMS providers?"), description: t("Prostay offers competitive pricing with no hidden fees, providing a cost-effective solution that scales with your business. Unlike some providers, we offer on-premise and cloud options with native apps for Windows, Mac, Android, and iPads, ensuring maximum flexibility.") }
                    ]}
                />

            </div>
        </div>
    );
}

export default Pricing;
